import { CloseTemplateViewConfig } from "@common/types/closeTemplateViewConfig";
import { createCounterpartiesViewConfig } from "@common/helpers/close-template-task-view-config";
import { omit } from "@common/helpers/objects";
import { AccountingLedgerBasis } from "@common/types/domains/accounting/ledger";
import { FiltersTypes } from "@common/types/filters/general";
import {
    CounterpartyDirectoryColumn,
    CounterpartyDirectoryView,
    CounterpartyDirectoryViewSchema,
} from "@common/types/views/counterpartyDirectoryView";
import { PersistableView } from "@common/types/views/views";
import { DirectoryFilters } from "@components/settings/Directory/lib";
import { parseTaskView } from "../shared/taskTableView";

export function defaultCounterpartiesView(): CounterpartyDirectoryView {
    return CounterpartyDirectoryViewSchema.parse(
        createCounterpartiesViewConfig(),
    );
}

export function parseCounterpartiesTaskView(
    raw: object | null | undefined,
): CounterpartyDirectoryView {
    return parseTaskView({
        schema: CounterpartyDirectoryViewSchema,
        raw,
        createDefaultView: defaultCounterpartiesView,
    });
}

const TEMPLATE_APPLY_TIME_KEYS = [
    "periodStart",
    "periodEnd",
    "entityIds",
    "counterpartiesIds",
] as const;

export function serializeCounterpartiesTemplateView(
    view: CounterpartyDirectoryView,
): CloseTemplateViewConfig {
    return {
        ...view,
        filters: omit(view.filters, [...TEMPLATE_APPLY_TIME_KEYS]),
    };
}

export function toDirectoryFilters(
    filters: FiltersTypes | undefined,
): DirectoryFilters {
    return {
        startDate: filters?.periodStart,
        endDate: filters?.periodEnd,
        entityIds: filters?.entityIds,
        search: filters?.search,
        basis: AccountingLedgerBasis.cash,
    };
}

export function withDirectoryScope(
    filters: FiltersTypes,
    scope: DirectoryFilters,
): FiltersTypes {
    return {
        ...filters,
        periodStart: scope.startDate,
        periodEnd: scope.endDate,
        entityIds: scope.entityIds,
        search: scope.search === "" ? undefined : scope.search,
    };
}

export const TASK_SCOPE_KEYS = [
    "periodStart",
    "periodEnd",
    "entityIds",
    "search",
] as const;

export function omitTaskScope(filters: FiltersTypes): FiltersTypes {
    return omit(filters, [...TASK_SCOPE_KEYS]);
}

export const PINNED_MEMBERSHIP_KEYS = [
    "isEligible1099",
    "counterpartyReviewedFields",
    "counterpartyReviewedFieldsMode",
    "counterpartyUnreviewedFields",
    "counterpartyUnreviewedFieldsMode",
    "counterpartyReviewerIds",
    "counterpartyReviewerMode",
    "disabledFilter",
] as const;

export function omitPinnedMembershipCriteria(
    filters: FiltersTypes,
): FiltersTypes {
    return omit(filters, [...PINNED_MEMBERSHIP_KEYS]);
}

interface BuildBulkTaskViewParams {
    view: CounterpartyDirectoryView;
    scope: DirectoryFilters;
    counterpartiesIds: string[];
}

export function buildBulkCounterpartiesTaskView({
    view,
    scope,
    counterpartiesIds,
}: BuildBulkTaskViewParams): PersistableView {
    return {
        ...omit(view, ["originalFilters"]),
        columns: view.columns.filter(
            (column) => column.id !== CounterpartyDirectoryColumn.ACTIONS,
        ),
        filters: {
            ...omitPinnedMembershipCriteria(
                withDirectoryScope(view.filters, scope),
            ),
            counterpartiesIds,
        },
        options: { ...view.options, selection: true, bulkActions: true },
    };
}
